import React from 'react';
import { Outlet } from 'react-router-dom';
import { AxiosError } from 'axios';
import { authorizeCanva, isCanvaAuthorized } from '../../providers/api/canva-auth/canvaAuthProvider';
import { API_MARKETING_PATH, getMarketingClient } from '../../providers/api/api';

export interface CanvaAuthContextValue {
  isAuthorized: boolean,
  login: (redirectUrl: string) => Promise<void>,
  isAuthorizationLoading?: boolean,
  authorizationError?: unknown,
}

export const CanvaAuthContext = React.createContext<CanvaAuthContextValue>({ isAuthorized: false, login: async () => { }, isAuthorizationLoading: true });

export const useCanvaAuthContext = () => React.useContext(CanvaAuthContext);

export const CanvaAuthContextProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const [isAuthorized, setIsAuthorized] = React.useState(false);
  const [isAuthorizationLoading, setIsAuthorizationLoading] = React.useState(true);
  const [authorizationError, setAuthorizationError] = React.useState<unknown>();

  React.useEffect(() => {
    let active = true;
    let removeInterceptor: VoidFunction | undefined;

    const checkAuthorization = async () => {
      try {
        const { status } = await isCanvaAuthorized();
        if (active) {
          setIsAuthorized(status === 'AUTHORIZED');
          setAuthorizationError(undefined);
        }
      } catch (error) {
        if (active) setAuthorizationError(error);
      } finally {
        if (active) setIsAuthorizationLoading(false);
      }
    };

    const initialize = async () => {
      try {
        const client = await getMarketingClient();
        if (!active) return;

        const interceptor = client.interceptors.response.use(undefined, async (error: AxiosError) => {
          const path = error.config?.url?.split('?')[0];
          if (active && error.response?.status === 401
            && path?.startsWith(`${API_MARKETING_PATH}/`)
            && path !== `${API_MARKETING_PATH}/is-authorized`) {
            await checkAuthorization();
          }
          return Promise.reject(error);
        });
        removeInterceptor = () => client.interceptors.response.eject(interceptor);

        await checkAuthorization();
      } catch (error) {
        if (active) {
          setAuthorizationError(error);
          setIsAuthorizationLoading(false);
        }
      }
    };

    initialize();

    return () => {
      active = false;
      removeInterceptor?.();
    };
  }, []);

  const login = React.useCallback(async (redirectUrl: string) => {
    const response = await authorizeCanva(redirectUrl);
    if (response?.url) {
      window.location.href = response.url;
    } else {
      throw new Error('URL di autorizzazione mancante');
    }
  }, []);

  const contextValue = React.useMemo(
    () => ({
      isAuthorized, login, isAuthorizationLoading, authorizationError,
    }),
    [isAuthorized, login, isAuthorizationLoading, authorizationError],
  );

  return (
    <CanvaAuthContext.Provider value={contextValue}>
      {children ?? <Outlet />}
    </CanvaAuthContext.Provider>
  );
};

export default CanvaAuthContextProvider;
